import { I18n } from 'react-redux-i18n';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { searchApplicants } from 'service/api';
import { updateLessonEntry } from '../service/api';
import { hideModal, showModal } from './modal';
import { COURSE_DATA_SUCCESS } from './user';

// Constants - action names
export const SEARCH_APPLICANTS = 'pv-kultu/applicantLessons/SEARCH_APPLICANTS';
export const SEARCH_APPLICANTS_SUCCESS = 'pv-kultu/applicantLessons/SEARCH_APPLICANTS_SUCCESS';
export const SEARCH_APPLICANTS_FAILED = 'pv-kultu/applicantLessons/SEARCH_APPLICANTS_FAILED';
export const SEARCH_RESULTS = 'pv-kultu/applicantLessons/SEARCH_RESULTS';
export const UPDATE_SEARCH_TERMS = 'pv-kultu/applicantLessons/UPDATE_SEARCH_TERMS';

export const UPDATE_APPLICANTS = 'pv-kultu/applicantLessons/UPDATE_APPLICANTS';
export const MARK_THEORY_DONE = 'pv-kultu/applicantLessons/MARK_THEORY_DONE';
export const MARK_THEORY_DONE_SUCCESS = 'pv-kultu/applicantLessons/MARK_THEORY_DONE_SUCCESS';
export const MARK_THEORY_DONE_FAILED = 'pv-kultu/applicantLessons/MARK_THEORY_DONE_FAILED';
export const RESET_APPLICANTS = 'pv-kultu/applicantLessons/RESET_APPLICANTS';

// Helpers
//--

// Initial state
const initialState = {
  applicants: undefined,
  results: undefined,
  loading: {
    applicants: false,
    saving: false
  },
  error: undefined,
  success: false,
  tableType: 'THEORY_STATUS',
  searchTerms: {
    lessonType: '',
    examClass: '',
    lessonIds: [],
    searchMissingLessons: false,
    applicantLessonStatus: '',
    courseNumber: '',
    courseYear: '',
    courseUnit: '',
    applicantName: ''
  },
  searchTermsChanged: false,
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SEARCH_APPLICANTS:
      return Object.assign({}, state, {
        loading: { applicants: true },
        error: undefined,
      });
    case SEARCH_APPLICANTS_SUCCESS:
      return Object.assign({}, state, {
        loading: {applicants: false},
        applicants: action.applicants,
        tableType: action.tableType,
      });
    case SEARCH_APPLICANTS_FAILED:
      return Object.assign({}, state, {
        loading: {applicants: false},
        error: action.error
      });
    case SEARCH_RESULTS:
      return Object.assign({}, state, {
        results: action.results
      });
    case UPDATE_APPLICANTS:
      return Object.assign({}, state, {
        applicants: action.applicants
      });
    case MARK_THEORY_DONE:
      return Object.assign({}, state, {
        loading: {saving: true},
        success: false
      });
    case MARK_THEORY_DONE_SUCCESS:
      return Object.assign({}, state, {
        loading: {saving: false},
        success: true
      });
    case MARK_THEORY_DONE_FAILED:
      return Object.assign({}, state, {
        loading: {saving: false},
        error: action.error,
        success: false
      });
    case UPDATE_SEARCH_TERMS:
      return Object.assign({}, state,{ searchTerms: Object.assign({}, state.searchTerms, action.terms, {searchTermsChanged: true})});
    case RESET_APPLICANTS:
      return Object.assign({}, state, {
        loading: {applicants: false},
        applicants: [],
      });
    default:
      return state;
  }
}

// Actions
export function searchApplicantsAction(payload) {
  return {type: SEARCH_APPLICANTS, payload};
}

export function searchApplicantsSuccess(data, tableType) {
  return {type: SEARCH_APPLICANTS_SUCCESS, applicants: data, tableType: tableType};
}

export function searchApplicantsFailed(error) {
  return {type: SEARCH_APPLICANTS_FAILED, error};
}

export function updateApplicantsAction(data) {
  return {type: UPDATE_APPLICANTS, applicants: data};
}

export function markTheoryLessonEntryDone(lessonEntry) {
  return {type: MARK_THEORY_DONE, lessonEntry};
}

export function markTheoryLessonEntryDoneSuccess() {
  return {type: MARK_THEORY_DONE_SUCCESS};
}

export function markTheoryLessonEntryDoneFailed(error) {
  return {type: MARK_THEORY_DONE_FAILED, error};
}

export function updateSearchTermsAction(terms) {
  return {type: UPDATE_SEARCH_TERMS, terms}
}

export function resetApplicantsAction() {
  return {type: RESET_APPLICANTS}
}

// Sagas
export function* handleSearchApplicantLessonsSaga(action) {
  try {
    let onlyUndone = action.payload.applicantLessonStatus === "UNDONE";
    const options = {
      contentType: 'application/json',
      queryParams: {
        'statusTable':!onlyUndone,
        'undoneTable':onlyUndone,
        'extended':true
      }
    };
    const payload = action.payload;
    payload.searchCourseByApplicant = true;
    if (payload.lessonIds && payload.lessonIds.length === 1 && payload.lessonIds[0] === "") {
      payload.lessonIds = null;
    }
    let data = yield call(searchApplicants, payload, options);


    if (onlyUndone) {
      data = mergeLessonsByApplicant(data);
    }

    yield put(searchApplicantsSuccess(data, onlyUndone ? "UNDONE_STATUS" : "THEORY_STATUS"));
    if (!data || data.length <= 0) {
      const modalProps = {
        messageType: 'ERROR',
        message: I18n.t('message.no_applicants_found')
      };
      yield put(showModal('MESSAGE', modalProps));
    }
  } catch (error) {
      yield put(searchApplicantsFailed(error));
      if (error.message === 'Unauthorized') {
        yield put(showModal('LOGIN'));
    }
  }
}

const mergeLessonsByApplicant = (applicants) => {

  let mergedDataMap = new Map();

  applicants.forEach(applicant => { if (mergedDataMap.get(applicant.trafiId)) {
                                      mergedDataMap.get(applicant.trafiId).lessons.push(applicant.lessonEntry);
                                    } else {
                                      applicant.lessons = [applicant.lessonEntry];
                                      delete applicant.lessonEntry;
                                      mergedDataMap.set(applicant.trafiId, applicant);
                                    }
  });

  return [...mergedDataMap.values()];
};

export function* handleMarkTheoryDoneSaga(action) {
  try {
    const options = {
      queryParams: {
        lessonStatus: 'COMPLETED'
      }
    };
    const newDateString = action.lessonEntry.entry.newDate.format('YYYY-MM-DD');
    const lessonEntryEntityId = action.lessonEntry.entry.lessonEntryEntityId;
    let applicants = action.lessonEntry.applicants;
    applicants.forEach(a => a.lessons = a.lessons.filter(lesson => lesson.entityId !== lessonEntryEntityId));

    yield call(updateLessonEntry, lessonEntryEntityId, newDateString, {}, options);
    yield put(markTheoryLessonEntryDoneSuccess());
    yield put(updateApplicantsAction(applicants.filter(a => a.lessons.length > 0 )));
    yield put(hideModal());
    const modalProps = {
      messageType: 'SUCCESS',
      message: I18n.t('message.update_lesson_entry_success')
    };
    yield put(showModal('MESSAGE', modalProps));
  } catch (error) {
    yield put(markTheoryLessonEntryDoneFailed('message.update_lesson_entry_error'));
    const modalProps = {
      messageType: 'ERROR',
      message: I18n.t('message.add_entries_failed')
    };
    yield put(showModal('MESSAGE', modalProps));
  }
}

export function* updateCourseToSearchTermsSaga(action) {
  yield put(updateSearchTermsAction({ courseNumber: action.courseData.courseNumber, courseUnit: action.courseData.courseUnit, courseYear: action.courseData.courseYear }))
}

function* watchSearch() {
  yield takeEvery(SEARCH_APPLICANTS, handleSearchApplicantLessonsSaga);
  yield takeEvery(MARK_THEORY_DONE, handleMarkTheoryDoneSaga);
  yield takeLatest(COURSE_DATA_SUCCESS, updateCourseToSearchTermsSaga)
}

export function* applicantsLessonsSagas() {
  yield all([
    watchSearch(),
  ]);
}


// WEBPACK FOOTER //
// ./src/ducks/applicantsLessonStatus.js